import {
  ServiceItemType,
  PricingModelOption,
  ServiceItemApiPayload,
  ServiceItem,
  ServiceItemDetail,
  ServiceItemsOrderPayload,
  ProductFormData,
  ProductV2,
  Pricing,
  ProductBundle,
  ProductBundleInput,
  ProductBundlesList
} from "./types";
import useAPI, {
  useAPIMutation,
  useMultipleAPIs,
  createAuthRetryHandler,
  // Plain async fetch helper (not a hook despite the name) — alias so the
  // rules-of-hooks lint doesn't flag the on-demand call inside useCallback.
  useAPIHelper as fetchAPIData
} from "@/services/api/hook";
import type { QueryKeyArray } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import { useAuth } from "@/services/auth/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import axios from "axios";

const PRODUCTS_ENDPOINT = "settings/products";
const PRODUCT_BUNDLES_ENDPOINT = "settings/products/bundles";

// A service write can change its bundles, so refetch them.
const useInvalidateBundles = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [PRODUCT_BUNDLES_ENDPOINT] });
};

export const useServiceItemTypes = (): ExtendedUseQueryResult<
  ServiceItemType[]
> => {
  return useAPI("settings/products/service_items/types");
};

export const usePricingModelOptions = (): ExtendedUseQueryResult<
  PricingModelOption[]
> => {
  return useAPI("settings/products/pricing_models");
};

export const useCreateServiceItem = () => {
  return useAPIMutation<
    {
      pathParams: {
        productId: string;
      };
      data: Omit<ServiceItemApiPayload, "pricing"> & {
        pricing: Omit<Pricing, "id">[];
      };
    },
    ServiceItem
  >("settings/products/service_items", {
    method: "POST"
  });
};

export const useUpdateServiceItem = () => {
  const invalidateBundles = useInvalidateBundles();

  return useAPIMutation<
    {
      pathParams: {
        productId: string;
        serviceItemId: string;
      };
      data: ServiceItemApiPayload;
    },
    ServiceItem
  >(
    "settings/products/service_items/update",
    { method: "PUT" },
    invalidateBundles
  );
};

export const useUpdateServiceItemPricing = () => {
  const invalidateBundles = useInvalidateBundles();

  return useAPIMutation<
    {
      pathParams: {
        productId: string;
        serviceItemId: string;
      };
      data: { pricing: Pricing[] };
    },
    ServiceItem
  >(
    "settings/products/service_items/pricing",
    { method: "PUT" },
    invalidateBundles
  );
};

export const useCreateServiceItemPricing = () => {
  const invalidateBundles = useInvalidateBundles();

  return useAPIMutation<
    {
      pathParams: {
        productId: string;
        serviceItemId: string;
      };
      data: { pricing: Omit<Pricing, "id">[] };
    },
    ServiceItem
  >(
    "settings/products/service_items/pricing",
    { method: "POST" },
    invalidateBundles
  );
};

export const useUpdateServiceItemsOrder = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    {
      pathParams: {
        productId: string;
      };
      data: ServiceItemsOrderPayload;
    },
    void
  >(
    "settings/products/service_items/order",
    { method: "PUT" },
    // Runs even if the page unmounted mid-save, so the cache never keeps the old order.
    () => {
      queryClient.invalidateQueries({ queryKey: ["settings/products"] });
    }
  );
};

export const useDeleteServiceItem = () => {
  const invalidateBundles = useInvalidateBundles();

  return useAPIMutation<
    {
      pathParams: {
        productId: string;
        serviceItemId: string;
      };
    },
    void
  >(
    "settings/products/service_items/delete",
    { method: "DELETE" },
    invalidateBundles
  );
};

export const useCreateProduct = () => {
  return useAPIMutation<
    {
      data: ProductFormData;
    },
    ProductV2
  >("productCreate", {
    method: "POST"
  });
};

export const useUpdateProduct = () => {
  return useAPIMutation<
    {
      pathParams: {
        productId: string;
      };
      data: ProductFormData;
    },
    ProductV2
  >("products/update", {
    method: "PUT"
  });
};

// Both by-id lookups below must build this exact key so they share a cache entry.
const SERVICE_ITEM_BY_ID_ENDPOINT = "settings/products/service_items/byId";
const serviceItemByIdOptions = (productId: string, serviceItemId: string) => ({
  method: "GET",
  pathParams: { productId, serviceItemId },
  queryParams: { include_deleted: "true" }
});

const toResolvedServiceItem = (
  serviceItem: ServiceItemDetail | null | undefined
): ServiceItemDetail | null => {
  if (!serviceItem) return null;
  // A legacy item from before the pricing-model/charge-type fields were
  // required can have either null in the backend
  // (ServiceItemDetailOutput types them Optional). Treating it as
  // "not found" routes it into the same serviceItemUnavailable fallback
  // as a genuine 404, rather than silently mis-rendering an unknown
  // pricing model/charge type as a real one.
  if (serviceItem.pricingModel == null || serviceItem.chargeType == null) {
    return null;
  }
  // A null item order only affects the item's position in the catalog,
  // never how a contract line built on it is edited, so it defaults to 0
  // rather than rejecting the item.
  return {
    ...serviceItem,
    itemOrder: serviceItem.itemOrder ?? 0
  };
};

// Fallback for a service item the bulk catalog fetch (settings/products)
// doesn't return because it's been soft-deleted — the bulk endpoint never
// includes deleted items, so an existing revenue item referencing one can't
// resolve its service item there. Only usable from an event handler (not
// render), since it's an on-demand fetch rather than a query subscription.
export const useResolveServiceItemById = (): ((
  productId: string,
  serviceItemId: string
) => Promise<ServiceItemDetail | null>) => {
  const { token, tenantId, tokenExpiry, login } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(
    async (productId: string, serviceItemId: string) => {
      const queryKey: QueryKeyArray = [
        SERVICE_ITEM_BY_ID_ENDPOINT,
        serviceItemByIdOptions(productId, serviceItemId),
        undefined
      ];
      try {
        const serviceItem = await queryClient.fetchQuery({
          queryKey,
          queryFn: () =>
            fetchAPIData({ token, tenantId, tokenExpiry }, queryKey),
          retry: createAuthRetryHandler(login)
        });
        return toResolvedServiceItem(serviceItem);
      } catch (error) {
        // A confirmed 404 means the item genuinely doesn't exist; anything
        // else (network, auth, server error) must propagate so the caller
        // doesn't report a false "not found".
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    [queryClient, token, tenantId, tokenExpiry, login]
  );
};

// Render-time counterpart of useResolveServiceItemById for rows whose service
// item the bulk catalog no longer returns. Shares its query key, so a later
// edit-drawer lookup for the same item is served from cache.
export const useDeletedServiceItems = (
  productId: string | undefined,
  serviceItemIds: string[]
): ServiceItemDetail[] => {
  const configs = useMemo(
    () =>
      productId
        ? serviceItemIds.map((serviceItemId) => ({
            endpoint: SERVICE_ITEM_BY_ID_ENDPOINT,
            options: serviceItemByIdOptions(productId, serviceItemId)
          }))
        : [],
    [productId, serviceItemIds]
  );
  const results = useMultipleAPIs(configs);
  // useQueries returns a new array every render; key the memo on each id's data.
  const dataSignature = results
    .map((result, index) => `${serviceItemIds[index]}:${result.dataUpdatedAt}`)
    .join("|");

  return useMemo(
    () =>
      productId
        ? results.flatMap((result) => {
            const serviceItem = toResolvedServiceItem(
              result.data as ServiceItemDetail | undefined
            );
            return serviceItem ? [serviceItem] : [];
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSignature, productId]
  );
};

export const useDeleteProduct = () => {
  return useAPIMutation<
    {
      pathParams: {
        productId: string;
      };
    },
    void
  >("products/delete", {
    method: "DELETE"
  });
};

// A bundle write changes each service's inBundles, so refetch products too.
const useInvalidateBundlesAndProducts = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [PRODUCT_BUNDLES_ENDPOINT] });
    queryClient.invalidateQueries({ queryKey: [PRODUCTS_ENDPOINT] });
  };
};

export const useProductBundles = (
  productId: string,
  { enabled = true }: { enabled?: boolean } = {}
): ExtendedUseQueryResult<ProductBundlesList> => {
  const options = useMemo(
    () => ({ method: "GET", pathParams: { productId } }),
    [productId]
  );
  return useAPI(PRODUCT_BUNDLES_ENDPOINT, options, { enabled });
};

export const useCreateProductBundle = () => {
  const invalidate = useInvalidateBundlesAndProducts();

  return useAPIMutation<
    { pathParams: { productId: string }; data: ProductBundleInput },
    ProductBundle
  >(PRODUCT_BUNDLES_ENDPOINT, { method: "POST" }, invalidate);
};

export const useUpdateProductBundle = () => {
  const invalidate = useInvalidateBundlesAndProducts();

  return useAPIMutation<
    {
      pathParams: { productId: string; bundleId: string };
      data: ProductBundleInput;
    },
    ProductBundle
  >("settings/products/bundles/update", { method: "PUT" }, invalidate);
};

export const useDeleteProductBundle = () => {
  const invalidate = useInvalidateBundlesAndProducts();

  return useAPIMutation<
    {
      pathParams: {
        productId: string;
        bundleId: string;
      };
    },
    void
  >(
    "settings/products/bundles/byId",
    { method: "DELETE" },
    // Runs even if the page unmounted mid-delete, so the list never keeps the bundle.
    invalidate
  );
};
